import type {
  ExportParams,
  ServerExportStatus,
} from 'lib/hooks/useServerExport';
import { ExportSpatialData } from './export-spatial-data';
import { SpatialExportBanner } from './spatial-export-banner';
import type { SpatialExportSelection, SpatialLayerOption } from './types';

/**
 * The request a single-layer spatial-export surface submits: the chosen formats, the surface's current
 * filter params (scoping the export to exactly what the table shows), and the chosen attribute ids
 * ([] = geometry-only). Intersected with `ExportParams` so it is assignable to each surface's
 * `startExport` (which carries that type's index signature). The single-layer surfaces (Areas, Type
 * Curves) all submit this shape — their per-surface *SpatialExportRequest types alias it.
 */
export type SingleLayerSpatialExportRequest = ExportParams & {
  formats: string[];
  filters: Record<string, unknown>;
  attributes: string[];
};

// The slice of a `useSpatialExport` result a single-layer surface needs. Not exported: the per-surface
// hooks return a structurally compatible object, and only this file's props reference the type by name.
interface SpatialExportController {
  /** The surface's async export status, driving the floating status banner. */
  exportStatus: ServerExportStatus;
  /** Submits the built one-layer request. */
  startExport: (request: SingleLayerSpatialExportRequest) => void;
  /** Dismisses the status banner. */
  resetExport: () => void;
  /** True while a submit is in flight — disables the footer and shows the in-flight label. */
  isExporting: boolean;
  /**
   * The signed-in user id. The surface disables its Export menu trigger without one, because the
   * finished file is announced on the user's own channel and a service account has no one to notify.
   */
  userId?: number | null;
}

interface SingleLayerSpatialExportDialogProps {
  /** Whether the export dialog is open. The surface owns this so the trigger can live in a menu. */
  open: boolean;
  /** Closes the dialog (also called after a submit). */
  onClose: () => void;
  /** The single exportable layer (id, label, record count, attributes). */
  layer: SpatialLayerOption;
  /** The surface's current filter params; the export covers exactly this filtered set. */
  filters: Record<string, unknown>;
  /** The surface's `useSpatialExport` result (status + startExport + reset + user). */
  controller: SpatialExportController;
}

/**
 * The spatial-export dialog + floating status banner shared by the single-layer surfaces (Areas, Type
 * Curves). The dialog is controlled — the surface owns the `open` state so the trigger can be a
 * "Spatial Data" item inside the surface's shared "Export" {@link ExportMenuButton} (a menu item cannot
 * host the dialog, which must render as a sibling of the button). Builds the one-layer
 * `{ formats, filters, attributes }` request on submit. Each surface stays a thin wrapper that builds
 * its layer, calls its own `useSpatialExport` hook (different endpoint / Pusher event), and hands the
 * result in. Map Query is NOT a single-layer surface (it spans several datasets), so it hosts
 * {@link ExportSpatialData} directly instead.
 *
 * @param {SingleLayerSpatialExportDialogProps} props - Open state, the layer, filters, and export controller.
 * @return {JSX.Element} The status banner plus the export dialog when open.
 */
export function SingleLayerSpatialExportDialog({
  open,
  onClose,
  layer,
  filters,
  controller,
}: SingleLayerSpatialExportDialogProps) {
  const { exportStatus, startExport, resetExport, isExporting } = controller;

  return (
    <>
      <SpatialExportBanner status={exportStatus} onDismiss={resetExport} />
      {open && (
        <ExportSpatialData
          open={open}
          onClose={onClose}
          layers={[layer]}
          isExporting={isExporting}
          onExport={(selection: SpatialExportSelection) => {
            // One layer, so the selection carries at most that layer; its attributeIds may be a
            // subset or empty (geometry-only). Validation keeps Export disabled until one is checked.
            const attributes = selection.layers[0]?.attributeIds ?? [];
            onClose();
            startExport({ formats: selection.formats, filters, attributes });
          }}
        />
      )}
    </>
  );
}
